/**
 * <policy-section>
 *
 * Interactions
 *   1. Mobile themed dropdown — <details>-based disclosure; closes on
 *      outside click, Escape, or when an option link is activated.
 *   2. Optional AJAX content swap — when BOTH the theme-global page-transition
 *      setting is on AND this section has `data-ajax-transition="true"`, nav
 *      clicks fetch the destination via Shopify's Section Rendering API,
 *      replace the content column in place, and update history state. Any
 *      failure falls back to a regular full-page navigation.
 */
class PolicySection extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.ajaxEnabled = this.dataset.ajaxTransition === 'true';
    this.content = this.querySelector('[data-policy-content]');
    this.navLinks = this.querySelectorAll('[data-policy-nav-link]');
    this.mobileNav = this.querySelector('[data-policy-mobile-nav]');
    this.mobileCurrent = this.querySelector('[data-policy-mobile-current]');
    this.isSwapping = false;

    this.bindEvents();
  }

  bindEvents() {
    if (this.mobileNav) {
      // Close the dropdown when the user clicks an option (mouse or keyboard).
      this.navLinks.forEach((link) => {
        link.addEventListener('click', this.onMobileLinkActivate.bind(this));
      });
      this.boundOnDocumentClick = this.onDocumentClick.bind(this);
      this.boundOnKeydown = this.onKeydown.bind(this);
      document.addEventListener('click', this.boundOnDocumentClick);
      document.addEventListener('keydown', this.boundOnKeydown);
    }

    if (this.ajaxEnabled && this.themePageTransitionEnabled()) {
      this.navLinks.forEach((link) => {
        link.addEventListener('click', this.onNavLinkClick.bind(this));
      });
      window.addEventListener('popstate', this.onPopState.bind(this));
    }
  }

  disconnectedCallback() {
    if (this.boundOnDocumentClick) document.removeEventListener('click', this.boundOnDocumentClick);
    if (this.boundOnKeydown) document.removeEventListener('keydown', this.boundOnKeydown);
  }

  onMobileLinkActivate() {
    if (this.mobileNav && this.mobileNav.open) {
      this.mobileNav.open = false;
    }
  }

  onDocumentClick(event) {
    if (!this.mobileNav || !this.mobileNav.open) return;
    if (this.mobileNav.contains(event.target)) return;
    this.mobileNav.open = false;
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    if (!this.mobileNav || !this.mobileNav.open) return;
    this.mobileNav.open = false;
    const summary = this.mobileNav.querySelector('summary');
    if (summary) summary.focus();
  }

  /**
   * Respect theme-global page transition gate.
   * window.FoxTheme.settings.enable_page_transition is injected by Sleek's
   * theme.js initialisation. If we cannot read it, disable AJAX for safety.
   */
  themePageTransitionEnabled() {
    try {
      return Boolean(window.FoxTheme && window.FoxTheme.settings && window.FoxTheme.settings.enable_page_transition);
    } catch (_err) {
      return false;
    }
  }

  onNavLinkClick(event) {
    // Respect modifier keys and new-tab intent
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (event.button !== 0) return;

    const link = event.currentTarget;
    const url = link.getAttribute('href');
    if (!url || url.charAt(0) === '#') return;

    // Only intercept same-origin navigation
    let destination;
    try {
      destination = new URL(url, window.location.origin);
    } catch (_err) {
      return;
    }
    if (destination.origin !== window.location.origin) return;

    event.preventDefault();
    this.swapContent(destination.href).catch(() => {
      window.location.href = destination.href;
    });
  }

  onPopState(event) {
    if (event.state && event.state.policySection) {
      this.swapContent(window.location.href, { push: false }).catch(() => {
        window.location.reload();
      });
    }
  }

  /**
   * Fetch target URL, extract the same-id section's content column, swap in.
   * Throws on any failure so callers can fall back to full navigation.
   */
  async swapContent(url, opts = { push: true }) {
    if (this.isSwapping) return;
    this.isSwapping = true;
    this.setAttribute('data-ajax-loading', 'true');
    this.removeAttribute('data-ajax-entering');
    this.triggerPageTransitionBar();

    // Respect reduced-motion: skip the fade window
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fadeOutMs = prefersReducedMotion ? 0 : 250;

    try {
      // Kick off fetch and fade-out in parallel — whichever is slower wins
      const [response] = await Promise.all([
        fetch(url, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
        }),
        new Promise((resolve) => setTimeout(resolve, fadeOutMs)),
      ]);
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const nextSection = doc.querySelector('policy-section[data-section-id="' + this.sectionId + '"]');
      const nextContent = nextSection
        ? nextSection.querySelector('[data-policy-content]')
        : doc.querySelector('[data-policy-content]');

      if (!nextContent) throw new Error('No policy-section content found at ' + url);

      this.content.innerHTML = nextContent.innerHTML;

      // Sync active-link state and the mobile dropdown's displayed label
      const targetPath = new URL(url).pathname;
      let activeTitle = '';
      this.navLinks.forEach((link) => {
        const isActive = link.getAttribute('href') === targetPath;
        link.classList.toggle('is-active', isActive);
        if (isActive) {
          link.setAttribute('aria-current', 'page');
          if (!activeTitle) activeTitle = link.textContent.trim();
        } else {
          link.removeAttribute('aria-current');
        }
      });

      if (this.mobileCurrent && activeTitle) {
        this.mobileCurrent.textContent = activeTitle;
      }

      // Title from fetched doc
      const nextTitle = doc.querySelector('title');
      if (nextTitle) document.title = nextTitle.textContent;

      if (opts.push) {
        window.history.pushState({ policySection: true }, '', url);
      }

      // Scroll to top of content column
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Re-run Shopify animations / re-register custom elements inside new content
      this.reinitializeContent();

      // Toggle loading off + entering on so the fade-in animation plays
      this.removeAttribute('data-ajax-loading');
      this.setAttribute('data-ajax-entering', 'true');
      // Clear the entering flag once the animation completes
      setTimeout(() => this.removeAttribute('data-ajax-entering'), prefersReducedMotion ? 0 : 400);
    } finally {
      this.isSwapping = false;
      // Safety net: ensure loading is cleared even on error paths
      this.removeAttribute('data-ajax-loading');
    }
  }

  /**
   * Kick Sleek's top-bar progress indicator. Safe no-op when the element
   * or pub/sub channel is missing.
   */
  triggerPageTransitionBar() {
    const bar = document.querySelector('page-transition.loading-bar');
    if (bar && typeof bar.start === 'function') {
      bar.start();
      setTimeout(() => {
        if (typeof bar.stop === 'function') bar.stop();
      }, 600);
      return;
    }
    try {
      if (window.FoxTheme && window.FoxTheme.pubsub && typeof window.FoxTheme.pubsub.publish === 'function') {
        window.FoxTheme.pubsub.publish('page-transition:start');
      }
    } catch (_err) {
      /* no-op */
    }
  }

  /**
   * If the swapped-in fragment contains custom elements or motion targets,
   * invoke theme re-init hooks so they animate/attach behaviour.
   */
  reinitializeContent() {
    try {
      if (window.FoxTheme && typeof window.FoxTheme.initializeSections === 'function') {
        window.FoxTheme.initializeSections(this.content);
        return;
      }
      if (window.MotionElement && typeof window.MotionElement.initAll === 'function') {
        window.MotionElement.initAll(this.content);
      }
    } catch (_err) {
      /* no-op — content still shows, just without animation attachment */
    }
  }
}

if (!customElements.get('policy-section')) {
  customElements.define('policy-section', PolicySection);
}
